/**
 * Query performance conventions for the dashboard.
 *
 * Three patterns for fast page transitions:
 *
 * 1. STALE TIME — prevent refetching data that's still fresh.
 *    Use `STALE_TIMES` constants instead of hardcoding durations.
 *
 * 2. CACHE SEEDING — when a list page already has data for a detail page,
 *    pre-populate the detail query cache so it renders instantly.
 *    Use `utils.<router>.<proc>.setData()` to seed from list data.
 *
 * 3. HOVER PREFETCH — fire detail queries when the user hovers a list item,
 *    so data arrives before they click. Use `usePrefetchOnHover()`.
 *
 * ## When to use what
 *
 * | Pattern       | When                                    | Example                           |
 * |---------------|-----------------------------------------|-----------------------------------|
 * | staleTime     | Any list/detail query                   | Sources list, project list        |
 * | Cache seeding | List has most fields the detail needs   | Source row → source detail header  |
 * | Hover prefetch| Detail page makes its own query anyway  | Source row hover → getById         |
 *
 * ## Anti-patterns
 *
 * - Don't prefetch expensive queries (large content, embeddings) on hover
 * - Don't set staleTime > 60s for data that other users might modify
 * - Don't seed cache with partial data that will cause layout shift
 */

// ---------------------------------------------------------------------------
// Stale time constants
// ---------------------------------------------------------------------------

export const STALE_TIMES = {
  /** List pages — OK to show slightly stale data on back-navigation. */
  list: 30_000, // 30 seconds

  /** Detail pages — data should be fresh but not refetched on every re-render. */
  detail: 10_000, // 10 seconds

  /** Static/slow-changing data (models, config, user profile). */
  static: 5 * 60_000, // 5 minutes

  /** Real-time data (processing status, activity feed). */
  realtime: 0, // always refetch
} as const;
